import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Position {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
}

export interface LocationPermissionStatus {
  granted: boolean;
  denied: boolean;
  prompt: boolean;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LocationService {
  private positionSubject = new BehaviorSubject<Position | null>(null);
  private permissionSubject = new BehaviorSubject<LocationPermissionStatus>({
    granted: false,
    denied: false,
    prompt: true
  });

  public position$ = this.positionSubject.asObservable();
  public permission$ = this.permissionSubject.asObservable();

  constructor() {
    this.checkInitialPermission();
  }

  async checkInitialPermission(): Promise<void> {
    if ('permissions' in navigator) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        this.updatePermissionStatus(permission.state);
        
        permission.onchange = () => {
          this.updatePermissionStatus(permission.state);
        };
      } catch (error) {
        console.warn('Permission API not supported, checking geolocation directly');
        this.checkGeolocationSupport();
      }
    } else {
      this.checkGeolocationSupport();
    }
  }

  private checkGeolocationSupport(): void {
    if ('geolocation' in navigator) {
      this.permissionSubject.next({
        granted: false,
        denied: false,
        prompt: true
      });
    } else {
      this.permissionSubject.next({
        granted: false,
        denied: true,
        prompt: false,
        error: 'Geolocation is not supported by this browser'
      });
    }
  }

  private updatePermissionStatus(state: PermissionState): void {
    const status: LocationPermissionStatus = {
      granted: state === 'granted',
      denied: state === 'denied',
      prompt: state === 'prompt'
    };
    
    this.permissionSubject.next(status);
    
    if (status.granted) {
      this.getCurrentPosition();
    }
  }

  async requestPermission(): Promise<LocationPermissionStatus> {
    return new Promise((resolve) => {
      if (!('geolocation' in navigator)) {
        const status: LocationPermissionStatus = {
          granted: false,
          denied: true,
          prompt: false,
          error: 'Geolocation is not supported by this browser'
        };
        this.permissionSubject.next(status);
        resolve(status);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const status: LocationPermissionStatus = {
            granted: true,
            denied: false,
            prompt: false
          };
          this.permissionSubject.next(status);
          this.updatePosition(position);
          resolve(status);
        },
        (error) => {
          let status: LocationPermissionStatus;
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              status = {
                granted: false,
                denied: true,
                prompt: false,
                error: 'Permission denied by user'
              };
              break;
            case error.POSITION_UNAVAILABLE:
              status = {
                granted: false,
                denied: false,
                prompt: true,
                error: 'Location information unavailable'
              };
              break;
            case error.TIMEOUT:
              status = {
                granted: false,
                denied: false,
                prompt: true,
                error: 'Location request timed out'
              };
              break;
            default:
              status = {
                granted: false,
                denied: false,
                prompt: true,
                error: 'Unknown location error'
              };
          }
          
          this.permissionSubject.next(status);
          resolve(status);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    });
  }

  getCurrentPosition(): Observable<Position> {
    return new Observable(observer => {
      if (!('geolocation' in navigator)) {
        observer.error(new Error('Geolocation is not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = this.createPosition(position);
          this.positionSubject.next(pos);
          observer.next(pos);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    });
  }

  watchPosition(): Observable<Position> {
    return new Observable(observer => {
      if (!('geolocation' in navigator)) {
        observer.error(new Error('Geolocation is not supported'));
        return;
      }

      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          const pos = this.createPosition(position);
          this.positionSubject.next(pos);
          observer.next(pos);
        },
        (error) => {
          observer.error(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );

      return () => {
        navigator.geolocation.clearWatch(watchId);
      };
    });
  }

  private updatePosition(position: GeolocationPosition): void {
    const pos = this.createPosition(position);
    this.positionSubject.next(pos);
  }

  private createPosition(position: GeolocationPosition): Position {
    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      timestamp: position.timestamp
    };
  }

  getCurrentPositionData(): Position | null {
    return this.positionSubject.value;
  }

  getPermissionStatus(): LocationPermissionStatus {
    return this.permissionSubject.value;
  }
}
